/**
 * Tenant membership & classification overrides (WSRADM-0015).
 *
 * `GET`/`PUT /admin/membership-config` via `apiBase()`. Tenant is the JWT —
 * never a slug on the path, query, or header. Bearer only; no cookies, no CSRF.
 * Do not send `productFamilyByCategory` — the board owns mapping.
 * Family is not membership status; `derived` / `overridden` display as sent.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import { asBoolean, asText, isObject } from '@/api/parse';

export const MEMBERSHIP_KEYS = [
  'WStudio.membership.gracePeriodDays',
  'WStudio.membership.shortTermThresholdDays',
  'WStudio.membership.renewalDueSoonDays',
  'WStudio.membership.tenureEstablishedDays',
  'WStudio.membership.tenureLoyalDays',
  'WStudio.membership.valueMidThreshold',
  'WStudio.membership.valueHighThreshold',
] as const;

export type MembershipKey = (typeof MEMBERSHIP_KEYS)[number];

export const DAY_COUNT_KEYS: readonly MembershipKey[] = [
  'WStudio.membership.gracePeriodDays',
  'WStudio.membership.shortTermThresholdDays',
  'WStudio.membership.renewalDueSoonDays',
  'WStudio.membership.tenureEstablishedDays',
  'WStudio.membership.tenureLoyalDays',
];

export const THRESHOLD_KEYS: readonly MembershipKey[] = [
  'WStudio.membership.valueMidThreshold',
  'WStudio.membership.valueHighThreshold',
];

const MEMBERSHIP_KEY_SET = new Set<string>(MEMBERSHIP_KEYS);

export const FIELD_COPY_KEY: Record<MembershipKey, string> = {
  'WStudio.membership.gracePeriodDays': 'config.fields.gracePeriodDays',
  'WStudio.membership.shortTermThresholdDays': 'config.fields.shortTermThresholdDays',
  'WStudio.membership.renewalDueSoonDays': 'config.fields.renewalDueSoonDays',
  'WStudio.membership.tenureEstablishedDays': 'config.fields.tenureEstablishedDays',
  'WStudio.membership.tenureLoyalDays': 'config.fields.tenureLoyalDays',
  'WStudio.membership.valueMidThreshold': 'config.fields.valueMidThreshold',
  'WStudio.membership.valueHighThreshold': 'config.fields.valueHighThreshold',
};

export interface MembershipField {
  key: string;
  kind: 'param';
  derived: string | null;
  overridden: boolean;
}

export interface MembershipConfigDto {
  fields: MembershipField[];
}

export interface MembershipConfigWrite {
  overrides?: Array<{ key: string; value: string }>;
  clearOverrides?: string[];
}

export function isMembershipKey(key: string): key is MembershipKey {
  return MEMBERSHIP_KEY_SET.has(key);
}

function asDerived(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  return null;
}

function parseField(raw: unknown): MembershipField | null {
  if (!isObject(raw)) return null;
  const key = asText(raw['key']);
  if (key === null || !isMembershipKey(key)) return null;
  const overridden = asBoolean(raw['overridden']);
  if (overridden === null) return null;
  return {
    key,
    kind: 'param',
    derived: asDerived(raw['derived']),
    overridden,
  };
}

export function parseMembershipConfig(body: unknown): MembershipConfigDto {
  if (!isObject(body) || !Array.isArray(body['fields'])) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  const fields: MembershipField[] = [];
  for (const entry of body['fields']) {
    const field = parseField(entry);
    if (field !== null) fields.push(field);
  }
  return { fields };
}

/** GET /admin/membership-config */
export async function getMembershipConfig(): Promise<MembershipConfigDto> {
  const body = await request<unknown>('GET', '/admin/membership-config');
  return parseMembershipConfig(body);
}

/** PUT /admin/membership-config — seven keys only; omit empty arrays. */
export async function putMembershipConfig(
  write: MembershipConfigWrite,
): Promise<MembershipConfigDto> {
  const json: MembershipConfigWrite = {};
  if (write.overrides !== undefined && write.overrides.length > 0) {
    json.overrides = write.overrides;
  }
  if (write.clearOverrides !== undefined && write.clearOverrides.length > 0) {
    json.clearOverrides = write.clearOverrides;
  }
  const body = await request<unknown>('PUT', '/admin/membership-config', { json });
  return parseMembershipConfig(body);
}
